"use client";

import { Button } from "@ui/components/button";
import { cn } from "@ui/lib";
import { Loader2Icon, UploadIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ChangeEvent, RefObject } from "react";

export interface AssetUploadHandle {
	isUploading: boolean;
	openFilePicker: () => void;
	inputRef: RefObject<HTMLInputElement | null>;
	handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

/** Tabs on the left, Upload on the right. Shared so My Assets, the asset picker
 *  dialog and the generator history feeds can't drift apart. Each surface
 *  supplies its own tab list — only the layout is shared. `upload` is optional:
 *  the history feeds show tabs alone. */
export function AssetToolbar<Tab extends string>({
	tabs,
	activeTab,
	onTabChange,
	upload,
	className,
}: {
	tabs: readonly { id: Tab; label: string }[];
	activeTab: Tab;
	onTabChange: (tab: Tab) => void;
	upload?: AssetUploadHandle;
	className?: string;
}) {
	const t = useTranslations("assets");

	return (
		<div
			className={cn("flex items-center justify-between gap-3", className)}
		>
			{/* min-w-0 + 横滑：窄屏放不下 4 个 tab 时让它自己滚，
			    而不是把右边的 Upload 挤出屏幕 */}
			<div className="no-scrollbar flex min-w-0 gap-1 overflow-x-auto rounded-xl bg-base-200 p-1">
				{tabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						onClick={() => onTabChange(tab.id)}
						className={cn(
							"shrink-0 rounded-lg px-3 py-1.5 text-sm transition-colors sm:px-4",
							tab.id === activeTab
								? "bg-base-100 font-medium text-base-content shadow-sm"
								: "text-base-content/60 hover:text-base-content",
						)}
					>
						{tab.label}
					</button>
				))}
			</div>

			{upload && (
				<>
					<input
						ref={upload.inputRef}
						type="file"
						accept="image/*"
						multiple
						className="hidden"
						onChange={upload.handleChange}
					/>
					<Button
						type="button"
						variant="outline"
						size="sm"
						// `outline` defaults to the pink secondary colour — keep it neutral
						className="h-9 shrink-0 gap-1.5 border-base-300 text-base-content/70 hover:bg-base-200"
						disabled={upload.isUploading}
						onClick={upload.openFilePicker}
					>
						{upload.isUploading ? (
							<Loader2Icon className="h-4 w-4 animate-spin" />
						) : (
							<UploadIcon className="h-4 w-4" />
						)}
						{t("upload")}
					</Button>
				</>
			)}
		</div>
	);
}
